import { Briefcase, CheckSquare, FileSignature, FolderKanban, Handshake } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useClients } from "@/hooks/use-clients";
import { useProjects } from "@/hooks/use-projects";
import { useQuotes } from "@/hooks/use-quotes";
import { useReferralPartners } from "@/hooks/use-referrals";
import { useTasks } from "@/hooks/use-tasks";
import { useStatusMeta } from "@/hooks/use-task-statuses";
import { matchesQuery } from "./palette-data";
import { PaletteGroup, PaletteRow } from "./PaletteRow";

// ============================================================
// Ricerca globale live della Command Palette.
// Ogni sotto-componente monta il proprio hook SOLO se il
// modulo è accessibile (canAccess) e la query è significativa:
// i dati arrivano quasi sempre dalla cache TanStack.
// ============================================================

const MIN_QUERY_LEN = 2;
const MAX_PER_TYPE = 6;

interface SearchProps {
  query: string;
  onNavigate: (path: string) => void;
}

export function EntitySearchResults({ query, onNavigate }: SearchProps) {
  const { canAccess } = useAuth();

  if (query.trim().length < MIN_QUERY_LEN) return null;

  return (
    <>
      {canAccess("clients") && <ClientResults query={query} onNavigate={onNavigate} />}
      {canAccess("projects") && <ProjectResults query={query} onNavigate={onNavigate} />}
      {canAccess("tasks") && <TaskResults query={query} onNavigate={onNavigate} />}
      {canAccess("quotes") && <QuoteResults query={query} onNavigate={onNavigate} />}
      {canAccess("referrals") && <ReferralResults query={query} onNavigate={onNavigate} />}
    </>
  );
}

function ClientResults({ query, onNavigate }: SearchProps) {
  const { data: clients = [] } = useClients();
  const matches = clients.filter((c) => matchesQuery(query, c.name)).slice(0, MAX_PER_TYPE);
  if (matches.length === 0) return null;

  return (
    <PaletteGroup heading="Clienti">
      {matches.map((c) => (
        <PaletteRow
          key={c.id}
          value={`client:${c.id}`}
          icon={Briefcase}
          label={c.name}
          onSelect={() => onNavigate(`/admin/clients/${c.id}`)}
        />
      ))}
    </PaletteGroup>
  );
}

function ProjectResults({ query, onNavigate }: SearchProps) {
  const { data: projects = [] } = useProjects();
  const matches = projects
    .filter((p) => matchesQuery(query, p.name, p.client?.name))
    .slice(0, MAX_PER_TYPE);
  if (matches.length === 0) return null;

  return (
    <PaletteGroup heading="Lavori">
      {matches.map((p) => (
        <PaletteRow
          key={p.id}
          value={`project:${p.id}`}
          icon={FolderKanban}
          label={p.name}
          meta={p.client?.name ?? undefined}
          onSelect={() => onNavigate(`/admin/projects/${p.id}`)}
        />
      ))}
    </PaletteGroup>
  );
}

function TaskResults({ query, onNavigate }: SearchProps) {
  const { data: tasks = [] } = useTasks();
  const statusMeta = useStatusMeta();
  const matches = tasks.filter((t) => matchesQuery(query, t.title)).slice(0, MAX_PER_TYPE);
  if (matches.length === 0) return null;

  return (
    <PaletteGroup heading="Task">
      {matches.map((t) => (
        <PaletteRow
          key={t.id}
          value={`task:${t.id}`}
          icon={CheckSquare}
          label={t.title}
          meta={statusMeta.get(t.status).label}
          onSelect={() => onNavigate(`/admin/tasks?id=${t.id}`)}
        />
      ))}
    </PaletteGroup>
  );
}

function QuoteResults({ query, onNavigate }: SearchProps) {
  const { data: quotes = [] } = useQuotes();
  const matches = quotes
    .filter((q) => matchesQuery(query, q.title, q.client?.name))
    .slice(0, MAX_PER_TYPE);
  if (matches.length === 0) return null;

  return (
    <PaletteGroup heading="Preventivi">
      {matches.map((q) => (
        <PaletteRow
          key={q.id}
          value={`quote:${q.id}`}
          icon={FileSignature}
          label={q.title}
          meta={q.client?.name ?? undefined}
          onSelect={() => onNavigate("/admin/quotes")}
        />
      ))}
    </PaletteGroup>
  );
}

function ReferralResults({ query, onNavigate }: SearchProps) {
  const { data: partners = [] } = useReferralPartners();
  const matches = partners.filter((p) => matchesQuery(query, p.name)).slice(0, MAX_PER_TYPE);
  if (matches.length === 0) return null;

  return (
    <PaletteGroup heading="Referenti">
      {matches.map((p) => (
        <PaletteRow
          key={p.id}
          value={`referral:${p.id}`}
          icon={Handshake}
          label={p.name}
          onSelect={() => onNavigate("/admin/referrals")}
        />
      ))}
    </PaletteGroup>
  );
}
